import { put, call, cancelled, select } from 'redux-saga/effects';
import { get } from 'lodash';
import fetch from 'isomorphic-fetch';
import { toast } from 'react-toastify';
import * as httpStatus from 'http-status';

import { LOADERS } from 'containers/AppContainer/Loader/constants';
import {
  startLoading,
  stopLoading,
} from 'containers/AppContainer/Loader/action';

import { makeSelectLocale } from 'containers/LanguageProvider/selectors';
import { localTranslate } from 'containers/services/translate';

const defaultRequestOptions: RequestOptions = {
  method: 'GET',
  headers: {},
};
export interface RequestOptions {
  method: 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH';
  headers: HeadersInit;
  body?: string;
}

export interface ResponseError extends Error {
  message: string;
  response: {
    status: number;
    statusText: string;
    json: () => Promise<any>;
  };
}

export function* handleError(error: ResponseError): Generator<unknown> {
  let displayedError;
  const locale = yield select(makeSelectLocale());

  if (error.response) {
    let jsonResponse;

    try {
      jsonResponse = yield error.response.json();
    } catch (err) {
      jsonResponse = error.response;
    }

    displayedError =
      get(jsonResponse, ['data', 'error', 'response', 'Message']) ||
      get(jsonResponse, ['data', 'error', 'response', 'message']) ||
      get(jsonResponse, ['data', 'error', 'response', 'message', 'Message']) ||
      get(jsonResponse, ['data', 'error', 'stack']) ||
      get(jsonResponse, ['data', 'error', 'message']) ||
      get(jsonResponse, ['data', 'message']) ||
      get(jsonResponse, ['message']) ||
      'Une erreur technique est survenue';

    if ([httpStatus['401'], httpStatus['403']].includes(displayedError)) {
      displayedError = localTranslate(
        'error.unpermitted_access',
        locale as string,
      );
    }
  } else {
    displayedError = localTranslate('error.general_error', locale as string);
  }

  yield toast.error(displayedError);
  return error.response || error;
}

export function requestWithAuth(
  url: string,
  options: RequestOptions,
  parseJson = true,
) {
  const optionsWithAuth: RequestOptions = {
    ...options,
    headers: {
      ...options.headers,
    },
  };

  return requestUtils.request(url, optionsWithAuth, parseJson);
}

export const request = (
  url: string,
  options: RequestOptions,
  parseJson = true,
) => {
  const baseApiPath = requestUtils.getApiBasePath();

  return fetch(`${baseApiPath}${url}`, options)
    .then(checkStatus)
    .then(response => {
      return parseJson ? requestUtils.parseJSON(response) : response;
    });
};

export const getApiBasePath = (): string => {
  return process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_GATEWAY_REST_API_URL || 'http://localhost:8282'
    : process.env.REACT_APP_GATEWAY_REST_API_URL
    ? process.env.REACT_APP_GATEWAY_REST_API_URL
    : '';
};

export const checkStatus = (response: Response): Response => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error = new Error(response.statusText) as ResponseError;
  error.response = response;
  throw error;
};

export const parseJSON = (response: Response): Promise<any> | Response => {
  return response.status === 204 ? response : response.json();
};

export function* callApi(
  apiPath: string,
  defaultDataValue: string | object | undefined,
  requestOptions = {},
  callbackParameters = [],
  callbackAction?: (...args: any) => any,
  formatData?: (data: any) => any,
  receiveBadResponse = false,
  loaderKey = LOADERS.GLOBAL,
  callbackActionWithoutDispatch?: (...args: any) => any,
): Generator<unknown> {
  let errorResponse;
  try {
    let rawData;
    yield put(startLoading(loaderKey));
    if (apiPath) {
      try {
        const apiRequestCall = call(requestWithAuth, apiPath, {
          ...defaultRequestOptions,
          ...(requestOptions as RequestOptions),
        });
        rawData = yield apiRequestCall;
      } catch (err) {
        errorResponse = yield* handleError((err as unknown) as ResponseError);
        rawData = defaultDataValue;
      } finally {
        if (yield cancelled()) {
          yield put(stopLoading(loaderKey));
        }
      }
    } else {
      rawData = defaultDataValue;
    }
    if (!(errorResponse && receiveBadResponse)) {
      try {
        const formattedData = formatData ? formatData(rawData) : rawData;
        if (callbackAction) {
          if (callbackParameters) {
            const parameters = formattedData
              ? [...callbackParameters, formattedData]
              : callbackParameters;
            yield put(callbackAction(...parameters));
          } else {
            yield put(callbackAction(formattedData));
          }
        }
        if (callbackActionWithoutDispatch) {
          if (callbackParameters) {
            const parameters = formattedData
              ? [...callbackParameters, formattedData]
              : callbackParameters;
            yield callbackActionWithoutDispatch(...parameters);
          } else {
            yield callbackActionWithoutDispatch(formattedData);
          }
        }
      } catch (err) {
        errorResponse = yield* handleError((err as unknown) as ResponseError);
      } finally {
        if (yield cancelled()) yield put(stopLoading(loaderKey));
      }
      yield put(stopLoading(loaderKey));
    }
  } catch (err) {
  } finally {
    if (yield cancelled()) {
      yield put(stopLoading(loaderKey));
    }
  }

  return errorResponse;
}

export function* callApiNext(
  apiPath: string,
  defaultDataValue: string | object | undefined,
  requestOptions = {},
  callbackParameters = [],
  callbackAction?: (...args: any) => any,
  formatData?: (data: any) => any,
  receiveBadResponse = false,
  loaderKey = LOADERS.GLOBAL,
): Generator<unknown> {
  let errorResponse;
  let formattedData;
  try {
    let rawData;

    yield put(startLoading(loaderKey));
    if (apiPath) {
      try {
        const apiRequestCall = call(requestWithAuth, apiPath, {
          ...defaultRequestOptions,
          ...(requestOptions as RequestOptions),
        });
        rawData = yield apiRequestCall;
      } catch (err) {
        errorResponse = yield* handleError((err as unknown) as ResponseError);
        rawData = defaultDataValue;
      } finally {
        if (yield cancelled()) {
          yield put(stopLoading(loaderKey));
        }
      }
    } else {
      rawData = defaultDataValue;
    }
    if (!(errorResponse && receiveBadResponse)) {
      try {
        formattedData = formatData ? formatData(rawData) : rawData;
        if (callbackAction) {
          if (callbackParameters) {
            const parameters = formattedData
              ? [...callbackParameters, formattedData]
              : callbackParameters;
            yield put(callbackAction(...parameters));
          } else {
            yield put(callbackAction(formattedData));
          }
        }
      } catch (err) {
        errorResponse = yield* handleError((err as unknown) as ResponseError);
      } finally {
        if (yield cancelled()) yield put(stopLoading(loaderKey));
      }
      yield put(stopLoading(loaderKey));
    }
  } catch (err) {
  } finally {
    if (yield cancelled()) {
      yield put(stopLoading(loaderKey));
    }
  }

  return !!errorResponse
    ? { success: false, data: errorResponse }
    : { success: true, data: formattedData };
}

export const requestUtils = { getApiBasePath, request, checkStatus, parseJSON };
